import { Select, SelectItem } from '@heroui/react';
import { GripVertical, Trash2 } from 'lucide-react';
import React, { FC, useState } from 'react';

interface MenuDragDropSectionProps {
    title: string;
    items: string[];
    availableItems: Array<{ id: string; name: string }>;
    onChange: (items: string[]) => void;
}

export const MenuDragDropSection: FC<MenuDragDropSectionProps> = ({
  title,
  items,
  availableItems,
  onChange,
}) => {
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== index) {
      setDraggedOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== dropIndex) {
      const newItems = [...items];
      const [removed] = newItems.splice(draggedItem, 1);
      newItems.splice(dropIndex, 0, removed);
      onChange(newItems);
    }
    setDraggedItem(null);
    setDraggedOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOverIndex(null);
  };

  const handleRemove = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleAdd = (itemId: string) => {
    if (itemId && !items.includes(itemId)) {
      onChange([...items, itemId]);
    }
  };

  const unusedItems = availableItems.filter(
    (item) => !items.includes(item.id),
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4 shadow-sm">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">{title}</h3>

      {/* Lista wybranych elementów */}
      <div className="space-y-2 mb-4 min-h-[120px]">
        {items.length === 0 ? (
          <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
            <p className="text-sm">Przeciągnij elementy tutaj lub dodaj je używając selecta poniżej</p>
          </div>
        ) : (
          items.map((itemId, index) => {
            const item = availableItems.find((i) => i.id === itemId);
            const isDragging = draggedItem === index;
            const isOver = draggedOverIndex === index;

            return (
              <div
                  /* eslint-disable-next-line react/no-array-index-key */
                key={`${itemId}-${index}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`
                  flex items-center gap-3 p-3 bg-gray-50 rounded-lg border
                  transition-all cursor-move select-none
                  ${isDragging ? 'opacity-40 border-blue-400 scale-105' : 'border-gray-200'}
                  ${isOver ? 'border-t-4 border-t-blue-500 mt-2' : ''}
                  hover:bg-gray-100 hover:border-gray-300 hover:shadow-sm
                `}
              >
                <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded text-xs font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <span className="flex-1 text-gray-700 font-medium">
                  {item?.name || itemId}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="p-2 hover:bg-red-100 rounded-lg text-red-500 transition-colors flex-shrink-0"
                  title="Usuń element"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Select z dostępnymi elementami */}
      {unusedItems.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <Select
            size="lg"
            label="Dodaj element do menu"
            variant="bordered"
            radius="sm"
            labelPlacement="outside"
            placeholder="Wybierz element..."
            onChange={(e) => handleAdd(e.target.value)}
            className="max-w-full"
          >
            {unusedItems.map((item) => (
              <SelectItem key={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </Select>
        </div>
      )}

      {unusedItems.length === 0 && items.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            ✓ Wszystkie dostępne elementy zostały dodane
          </p>
        </div>
      )}
    </div>
  );
};
